import { useEffect, useRef } from "react";

/**
 * Refetches re-emit an unchanged failure as a new error object, so keying the
 * effect below on object identity would re-fire the handler (a toast by
 * default) on every emission and stack toasts for a single failure.
 */
function getErrorSignature(error: unknown): string | undefined {
  if (!error) {
    return undefined;
  }
  if (typeof error !== "object") {
    return String(error);
  }
  const { name, message, errors } = error as {
    name?: unknown;
    message?: unknown;
    errors?: unknown;
  };
  try {
    return JSON.stringify({ name, message, errors });
  } catch {
    return `${String(name)}:${String(message)}`;
  }
}

export function useQueryErrorEffect(
  error: unknown,
  onError?: (error: unknown) => void
) {
  const handler = useRef(onError);
  handler.current = onError;
  const latestError = useRef(error);
  latestError.current = error;
  const signature = getErrorSignature(error);
  useEffect(() => {
    if (latestError.current) {
      handler.current?.(latestError.current);
    }
  }, [signature]);
}

export function useQueryCompletedEffect(
  data: unknown,
  loading: boolean,
  onCompleted?: (data: any) => void
) {
  const handler = useRef(onCompleted);
  handler.current = onCompleted;
  useEffect(() => {
    if (!loading && data !== undefined) {
      handler.current?.(data);
    }
  }, [data, loading]);
}
